import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Marquee from "react-fast-marquee";
import { QrCode, Scan, Gamepad2, Trophy, Gift, ArrowRight, Zap, Sparkles, Menu, PlayCircle } from "lucide-react";
import Walkthrough from "@/components/Walkthrough";

const USE_CASES = [
  { e: "🎓", t: "Schools & Colleges" }, { e: "🏢", t: "Corporate Events" }, { e: "🎂", t: "Birthday Parties" },
  { e: "💍", t: "Weddings" }, { e: "🎪", t: "Festivals" }, { e: "🎤", t: "Conferences" },
  { e: "🏆", t: "Competitions" }, { e: "🎉", t: "Family Events" }, { e: "🛍", t: "Exhibitions & Trade Shows" },
];

const STEPS = [
  { n: "01", t: "Create an Arena", d: "Set up your event in under 5 minutes.", i: Sparkles },
  { n: "02", t: "Get 3 Links + QR", d: "Player, Game Master & Big Screen — auto-generated.", i: QrCode },
  { n: "03", t: "Participants Scan", d: "Browser opens. No app. No download.", i: Scan },
  { n: "04", t: "Play Challenges", d: "Quiz, Pixel Quest, Fortune Spin & more.", i: Gamepad2 },
  { n: "05", t: "Earn Cash", d: "Points land instantly in every Cash Wallet.", i: Zap },
  { n: "06", t: "Hall of Fame", d: "Live leaderboard updates as people play.", i: Trophy },
  { n: "07", t: "Redeem Rewards", d: "Unlock Bronze, Silver & Gold prizes.", i: Gift },
];

const GAMES = ["Quiz Arena", "Pixel Quest", "Fortune Spin", "Mystery Vault", "Rapid Fire", "Match Master", "Memory Challenge", "Target Master", "Who Am I?", "Team Battle"];

export default function Landing() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    api.get("/public/events/funfest2026/display").then((r) => setActivity(r.data.activity || [])).catch(() => {});
  }, []);

  const goCreate = () => { if (user) navigate("/dashboard"); else login(); };

  return (
    <div className="relative">
      {/* Nav */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-head font-black text-xl">
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center brut-hard"><Zap className="w-5 h-5 text-white" /></div>
            Arenafy
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
            <a href="#usecases" className="hover:text-foreground transition-colors">Use cases</a>
            <a href="#games" className="hover:text-foreground transition-colors">Games</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-full" onClick={login} data-testid="nav-login" disabled={loading}>{user ? "Dashboard" : "Log in"}</Button>
            <Button className="rounded-full font-bold" onClick={goCreate} data-testid="nav-create">Create Arena</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-secondary via-background to-background">
          <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(circle at 82% 12%, rgba(230,51,41,0.12), transparent 42%), radial-gradient(circle at 10% 90%, rgba(0,90,200,0.08), transparent 40%)" }} />
        </div>
        <div className="max-w-6xl mx-auto px-5 py-24 md:py-32">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card shadow-sm text-sm mb-6 animate-pop">
            <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" /> No App · No Download · Just Scan & Play
          </div>
          <h1 className="font-head font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            Turn Any Event Into a <span className="text-primary">Game</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl">
            Create interactive, gamified experiences for schools, weddings, festivals, corporate days and more. Scan → Play → Earn Cash → Win.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full font-bold text-base px-8 h-13 brut-hard" onClick={goCreate} data-testid="hero-create">
              Create Your Arena <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button size="lg" variant="secondary" className="rounded-full font-bold text-base px-8" onClick={() => navigate("/e/funfest2026")} data-testid="hero-demo">
              Try the Demo
            </Button>
            <Button size="lg" variant="outline" className="rounded-full font-bold text-base px-8" onClick={() => setVideoOpen(true)} data-testid="hero-video">
              <PlayCircle className="w-5 h-5 mr-1" /> Watch Video
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
            <button onClick={() => navigate("/mod/funfest2026")} className="underline underline-offset-4 hover:text-foreground" data-testid="demo-mod-link">Demo Game Master Console</button>
            <button onClick={() => navigate("/display/funfest2026")} className="underline underline-offset-4 hover:text-foreground" data-testid="demo-display-link">Demo Big Screen</button>
          </div>
        </div>
        <Walkthrough open={videoOpen} onClose={() => setVideoOpen(false)} />
        {/* Activity ticker */}
        <div className="border-y border-border bg-card py-3">
          <Marquee gradient={false} speed={45}>
            {(activity.length ? activity : [{ player_name: "Rahul", zone_name: "Quiz Arena", xp: 100 }]).map((a, i) => (
              <span key={i} className="mx-8 text-sm text-muted-foreground font-mono-num">🎉 {a.player_name} · {a.zone_name} <span className="text-cash">+{a.xp} Cash</span></span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Use cases */}
      <section id="usecases" className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-head font-black text-3xl md:text-4xl mb-2">One platform. Every event.</h2>
        <p className="text-muted-foreground mb-10 max-w-lg">From classrooms to boardrooms to dance floors — Arenafy adapts to your crowd. Here are just a few event types you can gamify:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {USE_CASES.map((u, i) => (
            <div key={i} className="brut p-6 cursor-default bg-secondary/40" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="text-4xl mb-3">{u.e}</div>
              <div className="font-head font-bold text-lg">{u.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works - bento */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-head font-black text-3xl md:text-4xl mb-10">How it works</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => {
            const I = s.i;
            return (
              <div key={i} className={`brut lift p-6 ${i === 0 ? "md:col-span-2 bg-primary/10 border-primary/40" : ""}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-num font-extrabold text-2xl text-muted-foreground">{s.n}</span>
                  <I className="w-6 h-6 text-primary" />
                </div>
                <div className="font-head font-bold text-xl mb-1">{s.t}</div>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Games */}
      <section id="games" className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-head font-black text-3xl md:text-4xl mb-2">10 game modes, ready to play</h2>
        <p className="text-muted-foreground mb-10">Digital games run themselves. Physical games get scored by Game Masters. Victory QRs claim Cash instantly.</p>
        <div className="flex flex-wrap gap-3">
          {GAMES.map((g, i) => (
            <span key={i} className="px-5 py-2.5 rounded-full border-2 border-neutral-300 bg-card font-head font-bold lift hover:bg-primary hover:text-white">{g}</span>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-head font-black text-3xl md:text-4xl mb-2">Free while we're in early access</h2>
        <p className="text-muted-foreground mb-10">Every event is <span className="text-neon-green font-semibold">completely free</span> right now — unlimited participants, no charges. Paid plans below will unlock later.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {[{ t: "Starter", p: "Free", d: "Unlimited participants", f: ["All 10 games", "3 experiences", "QR registration", "Leaderboard & rewards"] },
            { t: "Per Block", p: "₹500", d: "Each additional 50 participants", f: ["Everything in Starter", "Unlimited zones", "Analytics", "Teams & achievements"], soon: true },
            { t: "Scale", p: "Custom", d: "Large events & multi-arena", f: ["Priority support", "Custom terminology", "Platform admin", "Dedicated onboarding"], soon: true }].map((c, i) => (
            <div key={i} className={`brut p-7 relative ${c.soon ? "opacity-50 grayscale pointer-events-none select-none" : ""}`} aria-disabled={c.soon} data-testid={`pricing-card-${c.t.toLowerCase().replace(/\s+/g, "-")}`}>
              {c.soon && <span className="absolute top-4 right-4 text-[11px] font-bold uppercase tracking-wide bg-secondary text-muted-foreground px-2 py-1 rounded-full">Coming soon</span>}
              <div className="font-head font-bold text-xl mt-1">{c.t}</div>
              <div className="font-mono-num font-extrabold text-4xl my-3">{c.p}</div>
              <p className="text-sm text-muted-foreground mb-5">{c.d}</p>
              <ul className="space-y-2 text-sm">{c.f.map((x, j) => <li key={j} className="flex gap-2"><span className="text-neon-green">✓</span>{x}</li>)}</ul>
              {c.soon
                ? <Button className="w-full rounded-full mt-6 font-bold" variant="secondary" disabled tabIndex={-1}>Coming soon</Button>
                : <Button className="w-full rounded-full mt-6 font-bold" variant="default" onClick={goCreate}>Create Your Arena</Button>}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="brut-primary bg-gradient-to-br from-primary/20 to-neon-pink/10 p-10 md:p-16 text-center rounded-3xl">
          <h2 className="font-head font-black text-3xl md:text-5xl mb-4">Ready to level up your event?</h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">Signup → Arena → Shareable QR in under 5 minutes.</p>
          <Button size="lg" className="rounded-full font-bold text-base px-10 h-13" onClick={goCreate} data-testid="cta-create">Create Your Arena</Button>
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
        <div className="font-head font-black text-lg text-foreground mb-2">Arenafy</div>
        The easiest way to turn any event into an interactive game.
        <div className="mt-2">Support: <a className="text-primary" href="mailto:support@arenafy.app">support@arenafy.app</a></div>
      </footer>
    </div>
  );
}
